import type { HelpContent } from './types';

export const skillsHelp: HelpContent = {
  pageTitle: 'Skills',
  description: 'Browse and search the skills recorded across your organisation, see which resources hold each skill, and review proficiency levels.',

  functionality: {
    overview: 'The Skills page lists all skills recorded in Access Projects, organised by category. Search or filter to find a skill, then click it to see which resources hold it and their proficiency levels.',

    keyFeatures: [
      {
        label: 'Skills library',
        detail: 'A complete list of all skills recorded in Access Projects, organised by category.',
      },
      {
        label: 'Search and filter by category',
        detail: 'Find skills quickly by searching by name or filtering by category such as Technical, Design, Delivery, Leadership, or Domain / Sector.',
      },
      {
        label: 'Resource count',
        detail: 'Each skill shows how many resources hold that skill, giving you a quick view of capability depth across your organisation.',
      },
      {
        label: 'Proficiency levels',
        detail: 'View the proficiency level recorded for each resource against a skill, rated on a five-star scale.',
      },
      {
        label: 'Resource drawer',
        detail: 'Click any skill to open a side panel listing all resources who hold that skill, along with their role, proficiency level, and a link to view them in the Directory.',
      },
    ],

    sectionsAndFields: [
      {
        name: 'Search bar',
        description: 'Filter skills in real time by typing a skill name.',
      },
      {
        name: 'Category filter',
        description: 'Filter skills by category — Technical, Design, Delivery, Leadership, or Domain / Sector. Select "All" to see every skill.',
      },
      {
        name: 'Sort controls',
        description: 'Sort the skills list by skill name, category, or resource count in ascending or descending order.',
      },
      {
        name: 'Skill row',
        description: 'Displays the skill name, category, and the number of resources who hold that skill. Click to open the resource drawer.',
      },
      {
        name: 'Category badge',
        description: 'A colour-coded label showing which category the skill belongs to.',
      },
      {
        name: 'Resource drawer',
        description: 'A side panel that opens when you click a skill. Lists all resources who hold that skill, showing their name, role, proficiency level, and a link to view them in the Directory.',
      },
      {
        name: 'Proficiency rating',
        description: 'A five-star rating indicating the proficiency level of a resource for a given skill. One star is beginner, five stars is expert.',
      },
      {
        name: 'View in Directory link',
        description: 'A link within the resource drawer that takes you to the Resource Directory filtered to show resources with the selected skill.',
      },
    ],

    useCases: [
      'Finding resources with a specific skill before assigning them to a project.',
      'Understanding the depth of a capability across your organisation.',
      'Identifying skills gaps by reviewing which skills have few or no resources.',
      'Comparing proficiency levels across resources for a given skill.',
    ],
  },

  userGuide: {
    gettingStarted: 'The Skills page lists all skills recorded in Access Projects. Use the search bar or category filter to find a specific skill, then click it to see which resources hold that skill and their proficiency levels.',
    steps: [
      {
        title: 'How do I find a skill?',
        items: [
          { text: 'Use the search bar to filter skills by name.' },
          { text: 'Use the category filter to narrow results to a specific skill category.' },
          { text: 'Use the sort controls to order the list by name, category, or resource count.' },
        ],
      },
      {
        title: 'How do I see which resources hold a skill?',
        items: [
          { text: 'Click any skill row to open the resource drawer.' },
          { text: 'The drawer lists all resources who hold that skill, along with their role and proficiency level.' },
          { text: 'Click "View in Directory" to navigate to the Resource Directory filtered by that skill.' },
        ],
      },
    ],
    proTips: [
      'Sort by resource count to quickly identify your most common or most scarce capabilities.',
      'Use the category filter to focus on a specific discipline — for example, filter to "Technical" when planning a technically complex project.',
      'The "View in Directory" link in the resource drawer is a quick way to jump to a filtered view of the Resource Directory for a specific skill.',
    ],
  },

  faq: [
    {
      question: 'Can I add or edit skills from this page?',
      answer: 'Skills are managed centrally by an Administrator. If you need a new skill added or an existing one updated, contact your Administrator.',
    },
    {
      question: 'What does the resource count mean?',
      answer: 'The resource count shows how many people in your organisation have that skill recorded against their profile. It gives you a quick sense of capability depth.',
    },
    {
      question: 'What does the proficiency rating mean?',
      answer: 'Proficiency is rated on a five-star scale — one star indicates a beginner level and five stars indicates expert level. Ratings are set when skills are assigned to a resource.',
    },
    {
      question: 'Why does a skill show zero resources?',
      answer: 'A skill with zero resources means it has been added to the system but not yet assigned to anyone. Contact your Administrator if you believe this is incorrect.',
    },
    {
      question: 'How do I find all resources with a specific skill?',
      answer: 'Click the skill row to open the resource drawer, which lists everyone who holds that skill. You can also click "View in Directory" to see them in the full Resource Directory.',
    },
  ],

  relatedPages: [
    { label: 'Resource Directory', path: '/directory' },
    { label: 'Roles & Rates', path: '/roles-rates' },
  ],
};
